import { TradeTicketId } from "@goodfill/domain-properties";
import React, { useContext } from "react";
import { Button, InputCheckbox, Modal } from "@goodfill/ui-components";
import { arrayDeepEqual, toggleInArray } from "@goodfill/array";
import { PresentedPortfolioPosition } from "../PresentedPortfolioPosition";
import { BatchSellDialog } from "./BatchSellDialog";
import { Icon } from "@blueprintjs/core";

interface Props {
  selected: TradeTicketId[];
  setSelected: (value: TradeTicketId[]) => void;
  isDialogOpen: boolean;
  setIsDialogOpen: (vale: boolean) => void;
  onRowRemove: (rowId: TradeTicketId) => void;
}

export const SelectedToBatchSellContext = React.createContext<Props>({
  selected: [],
  setSelected: () => undefined,
  isDialogOpen: false,
  setIsDialogOpen: () => undefined,
  onRowRemove: () => undefined,
});

export const BatchSellButtons = ({ rows }: { rows: PresentedPortfolioPosition[] }) => {
  const { selected, setSelected, setIsDialogOpen } = useContext(SelectedToBatchSellContext);

  const allIds = rows
    .filter((a) => a.availableToSell.greaterThan(0))
    .map((a) => a.id)
    .sort();

  const isAnyAvailableToSell = allIds.length > 0;

  const isAllSelected = arrayDeepEqual(allIds, selected);

  return isAnyAvailableToSell ? (
    <div className="flex flex-row gap-2">
      <Button
        disabled={!isAnyAvailableToSell}
        size="small"
        fill
        primary={true}
        grouped
        onClick={() => setSelected(isAllSelected ? [] : allIds)}
      >
        Select {isAllSelected ? "none" : "all"}
      </Button>
      <Button
        size="small"
        fill
        red={true}
        grouped
        onClick={() => setIsDialogOpen(true)}
        disabled={selected.length === 0}
      >
        Sell {selected.length} selected
      </Button>
    </div>
  ) : null;
};

export const BatchSellRowCheckbox = ({ rowId }: { rowId: TradeTicketId }) => {
  const { selected, setSelected } = useContext(SelectedToBatchSellContext);

  return (
    <InputCheckbox
      small
      checked={selected.includes(rowId)}
      onChange={() => setSelected(toggleInArray(rowId, selected).sort())}
    />
  );
};

export const BatchSellModal = ({ rows }: { rows: readonly PresentedPortfolioPosition[] }) => {
  const { selected, isDialogOpen, setIsDialogOpen } = useContext(SelectedToBatchSellContext);

  return (
    <Modal isActive={isDialogOpen} setIsActive={(isActive) => setIsDialogOpen(isActive)}>
      {isDialogOpen ? <BatchSellDialog rows={rows.filter((row) => selected.includes(row.id))} /> : null}
    </Modal>
  );
};

export const BatchSellRemoveRow = ({ rowId, disabled }: { rowId: TradeTicketId; disabled: boolean }) => {
  const { onRowRemove } = useContext(SelectedToBatchSellContext);

  return (
    <Button fill onClick={() => onRowRemove(rowId)} disabled={disabled}>
      <Icon icon="trash" />
    </Button>
  );
};
